import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { naytiveAPI } from '../services/api';
import { VoiceAgentClient } from '../services/voiceAgentClient';
import { AudioLevelMeter } from '../components/AudioLevelMeter';
import Sidebar from '../components/Sidebar';
import './NaytiveApplications.css';

// Friendly rendering helpers — kept inline; if we end up duplicating these
// across more naytive admin pages we'll pull them into a shared util.
const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatPreferredSlot = (date, time, tz) => {
  if (!date && !time) return '—';
  const parts = [];
  if (date) parts.push(new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
  if (time) parts.push(time);
  return parts.join(' · ') + (tz ? `  (${tz})` : '');
};

const CALL_STATUS_LABEL = {
  queued: 'Scheduled',
  in_progress: 'In progress',
  answered: 'Completed',
  exhausted: 'Exhausted',
  cancelled: 'Cancelled'
};

const NaytiveApplications = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  // Incorporation feed lives alongside visa attorney_requests — the table
  // unifies the two with a `kind` tag so the page is one applications hub
  // instead of two parallel screens.
  const [bookings, setBookings] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeAttempts, setActiveAttempts] = useState(null); // { requestId, items, loading }
  const [callingId, setCallingId] = useState(null);
  const [kindFilter, setKindFilter] = useState('all'); // 'all' | 'visa' | 'incorporation'
  const [statusFilter, setStatusFilter] = useState('');

  // Live call modal — { requestId, sessionId, status, error }
  // status: 'starting' | 'connecting' | 'connected' | 'ended' | 'error'
  const [liveCall, setLiveCall] = useState(null);
  const audioRef = useRef(null);
  const clientRef = useRef(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      // Always fetch all three feeds — filtering happens client-side so
      // toggling between All / Visa / Incorporation is instant. Proposals
      // power the per-row PROPOSAL button on incorporation rows (booking
      // → proposal_id map).
      const [attyRes, bookRes, propRes] = await Promise.all([
        naytiveAPI.listAttorneyRequests().catch((e) => {
          console.error('Failed to fetch attorney requests:', e);
          return { data: { requests: [] } };
        }),
        naytiveAPI.listIncorporationBookings().catch((e) => {
          console.error('Failed to fetch incorporation bookings:', e);
          return { data: { bookings: [] } };
        }),
        naytiveAPI.listIncorporationProposals().catch((e) => {
          console.error('Failed to fetch incorporation proposals:', e);
          return { data: { items: [] } };
        }),
      ]);
      setRequests(attyRes.data?.requests || []);
      setBookings(bookRes.data?.bookings || []);
      setProposals(propRes.data?.items || []);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      setToast({ type: 'error', message: 'Could not load applications' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Unify visa + incorporation rows into a single list sorted newest
  // first. Each entry carries a `kind` so the table can pick the right
  // columns + per-row actions.
  const unified = useMemo(() => {
    const visaRows = requests.map((r) => ({
      ...r,
      kind: 'visa',
      targetName: r.visaName,
      targetSub: `${r.destinationName} (${r.destinationCode})`,
      sortKey: r.submittedAt || r.createdAt,
    }));
    const incorpRows = bookings.map((b) => ({
      ...b,
      kind: 'incorporation',
      targetName: b.jurisdictionName,
      targetSub: b.jurisdictionRegion || b.jurisdictionCountryCode || '',
      // Incorporation has no submitted_at; createdAt is the only timestamp.
      sortKey: b.createdAt,
    }));
    // Standalone proposals — the "Create a new entity" flow persists a
    // committed proposal WITHOUT a booking (booking_id NULL), so those cases
    // never appeared here (this list was booking-only). Surface them as
    // incorporation rows so ops sees every incorporation order in one place,
    // with the PROPOSAL button to review + execute it. Booking-backed
    // proposals are skipped (their booking already renders a row).
    const proposalRows = proposals
      .filter((p) => !p.booking_id)
      .map((p) => ({
        id: `prop-${p.id}`,
        kind: 'incorporation',
        standaloneProposal: true,
        proposalId: p.id,
        userName:
          [p.first_name, p.last_name].filter(Boolean).join(' ').trim() ||
          (p.user_email ? String(p.user_email).split('@')[0] : '—'),
        userEmail: p.user_email || '—',
        targetName: p.title || p.jurisdiction_name || p.jurisdiction_slug,
        targetSub: p.jurisdiction_name || p.country_code || '',
        status: p.status,
        preferredDate: null,
        preferredTime: null,
        userTimezone: null,
        sortKey: p.created_at,
      }));
    return [...visaRows, ...incorpRows, ...proposalRows].sort((a, b) => {
      const at = a.sortKey ? Date.parse(a.sortKey) : 0;
      const bt = b.sortKey ? Date.parse(b.sortKey) : 0;
      return bt - at;
    });
  }, [requests, bookings, proposals]);

  // Visible rows = unified filtered by kind + status chips.
  const visibleRows = useMemo(() => {
    return unified.filter((r) => {
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      return true;
    });
  }, [unified, kindFilter, statusFilter]);

  // Status options available in the filter — visa + incorp have slightly
  // different enums, so we surface the union (or kind-specific when one
  // kind is selected).
  const availableStatuses = useMemo(() => {
    const VISA = ['pending', 'reviewing', 'scheduled', 'completed', 'cancelled'];
    const INCORP = ['pending', 'contacted', 'scheduled', 'completed', 'cancelled'];
    if (kindFilter === 'visa') return VISA;
    if (kindFilter === 'incorporation') return INCORP;
    const seen = new Set();
    return [...VISA, ...INCORP].filter((s) => (seen.has(s) ? false : (seen.add(s), true)));
  }, [kindFilter]);

  const counts = useMemo(
    () => ({
      all: unified.length,
      visa: requests.length,
      incorporation: bookings.length,
    }),
    [unified, requests, bookings],
  );

  // Auto-dismiss toasts after 3.5s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleStartCall = async (id, kind = 'visa') => {
    setCallingId(id);
    try {
      // 1. Reserve a session on the voice-agent. The pipeline doesn't
      //    boot until the browser posts a WebRTC offer with the
      //    returned session_id. `kind` tells voice-agent which context
      //    loader to use (attorney_requests vs incorporation_bookings).
      const res = await naytiveAPI.startTestCall(id, kind);
      const sessionId = res.data.session_id;
      if (!sessionId) throw new Error('voice-agent did not return a session_id');

      // 2. Open the live-call modal — modal effect kicks off WebRTC.
      setLiveCall({ requestId: id, kind, sessionId, status: 'starting', error: null });
    } catch (err) {
      const detail = err?.response?.data?.message || err.message;
      setToast({ type: 'error', message: `Start call failed: ${detail}` });
    } finally {
      setCallingId(null);
    }
  };

  // Twilio path — voice-agent dials the user's stored phone instead
  // of opening a browser WebRTC session. No live modal needed here;
  // the audio flows through the phone. We toast a confirmation with
  // the masked phone + call_sid for traceability.
  const handleStartRealCall = async (id, kind = 'visa') => {
    setCallingId(id);
    try {
      const res = await naytiveAPI.startRealCall(id, kind);
      const { call_sid, masked_phone } = res.data || {};
      if (!call_sid) throw new Error('voice-agent did not return a call_sid');
      setToast({
        type: 'success',
        message: `Dialing ${masked_phone || 'user'}… (Twilio sid ${call_sid.slice(0, 10)}…)`,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err.message;
      setToast({ type: 'error', message: `Real call failed: ${detail}` });
    } finally {
      setCallingId(null);
    }
  };

  // Stream refs for the audio level meters (Fix #2 below).
  const [micStream, setMicStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  // Once the modal mounts with a sessionId, wire up the VoiceAgentClient.
  // IMPORTANT: depend ONLY on sessionId, NOT on status — otherwise this
  // effect re-runs when we update status ('starting' → 'connecting' →
  // 'connected'), the old effect's cleanup sets cancelled=true, and the
  // live client's callbacks then bail out on every state update. That
  // bug is why the modal got stuck on "Connecting to Kim…" forever.
  useEffect(() => {
    if (!liveCall?.sessionId) return;
    let cancelled = false;

    const client = new VoiceAgentClient({
      sessionId: liveCall.sessionId,
      onConnectionState: (state) => {
        if (cancelled) return;
        // iceConnectionState can be 'connected' OR 'completed' on success
        // (completed = all checks passed, connected = at least one pair).
        if (state === 'connected' || state === 'completed') {
          setLiveCall((prev) => prev && { ...prev, status: 'connected' });
        } else if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          setLiveCall((prev) => prev && prev.status !== 'ended' ? { ...prev, status: 'ended' } : prev);
        }
      },
      onRemoteStream: (stream) => {
        if (cancelled) return;
        setRemoteStream(stream);
        if (audioRef.current) {
          audioRef.current.srcObject = stream;
          audioRef.current.play().catch(() => {/* autoplay blocked = user must click */});
        }
      },
      onLocalStream: (stream) => {
        if (cancelled) return;
        setMicStream(stream);
      },
      onError: (msg) => {
        if (cancelled) return;
        setLiveCall((prev) => prev && { ...prev, status: 'error', error: String(msg) });
      }
    });
    clientRef.current = client;

    setLiveCall((prev) => prev && { ...prev, status: 'connecting' });

    client.start()
      .catch((err) => {
        if (cancelled) return;
        setLiveCall((prev) => prev && { ...prev, status: 'error', error: err?.message || String(err) });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCall?.sessionId]);

  const handleHangUp = () => {
    if (clientRef.current) {
      clientRef.current.stop();
      clientRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
    }
    setMicStream(null);
    setRemoteStream(null);
    setLiveCall(null);
    fetchRequests(); // pull any attempt rows the call wrote
  };

  const handleViewAttempts = async (id) => {
    setActiveAttempts({ requestId: id, items: [], loading: true });
    try {
      const res = await naytiveAPI.getAttempts(id);
      setActiveAttempts({ requestId: id, items: res.data.attempts || [], loading: false });
    } catch (err) {
      setActiveAttempts(null);
      setToast({ type: 'error', message: 'Could not load attempts' });
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">NAYTIVE APPLICATIONS</h1>
            <p className="page-subtitle">
              VISA REQUESTS + INCORPORATION BOOKINGS
            </p>
          </div>
          <div className="header-right">
            <button
              className="btn-primary"
              onClick={() => navigate('/create-company')}
              title="Create a company for a client + upload docs"
              style={{ marginRight: 8 }}
            >
              + New Company
            </button>
            <button
              className="btn-secondary"
              onClick={fetchRequests}
              disabled={loading}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
              </svg>
              REFRESH
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stat cards double as kind filter — clicking toggles the filter,
              matching how Vendors / Visa Applications etc. work. */}
          <div className="stats-grid stats-grid-3">
            <div
              className={`stat-card stat-card-clickable ${kindFilter === 'all' ? 'stat-card-active' : ''}`}
              onClick={() => setKindFilter('all')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL</span>
                <span className="stat-value">{counts.all}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-info stat-card-clickable ${kindFilter === 'visa' ? 'stat-card-active' : ''}`}
              onClick={() => setKindFilter('visa')}
            >
              <div className="stat-info">
                <span className="stat-label">VISA</span>
                <span className="stat-value">{counts.visa}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${kindFilter === 'incorporation' ? 'stat-card-active' : ''}`}
              onClick={() => setKindFilter('incorporation')}
            >
              <div className="stat-info">
                <span className="stat-label">INCORPORATION</span>
                <span className="stat-value">{counts.incorporation}</span>
              </div>
            </div>
          </div>

          <div className="dashboard-section">
            {/* Filter strip — type + status. Type filter is duplicated by the
                clickable stat-cards above; keeping a dropdown too so people
                who scan the toolbar can find it without guessing. */}
            <div className="na-toolbar">
              <div className="na-toolbar-filters">
                <select
                  className="na-status-select"
                  value={kindFilter}
                  onChange={(e) => {
                    setKindFilter(e.target.value);
                    setStatusFilter('');
                  }}
                  aria-label="Filter by application type"
                >
                  <option value="all">All types</option>
                  <option value="visa">Visa</option>
                  <option value="incorporation">Incorporation</option>
                </select>
                <select
                  className="na-status-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="">All statuses</option>
                  {availableStatuses.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <span className="na-toolbar-count">
                {visibleRows.length} showing
              </span>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading applications...</p>
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <p>No applications match the current filters.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>APPLICATION</th>
                      <th>APPLICANT</th>
                      <th>TARGET</th>
                      <th>STATUS</th>
                      <th>SLOT</th>
                      <th>CALL</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((r) => {
                      const isVisa = r.kind === 'visa';
                      const rowKey = `${r.kind}-${r.id}`;
                      return (
                        <tr key={rowKey}>
                          <td>
                            <div className="na-app-cell">
                              <span
                                className={`na-kind-pill ${isVisa ? 'na-kind-visa' : 'na-kind-incorp'}`}
                              >
                                {isVisa ? 'VISA' : 'ENTITY'}
                              </span>
                              <span className="na-app-id">#{r.id}</span>
                            </div>
                          </td>
                          <td>
                            <div className="na-applicant-cell">
                              <span className="na-applicant-name">{r.userName}</span>
                              <span className="na-applicant-email">{r.userEmail || '—'}</span>
                              {(r.jobTitle || r.company) && (
                                <span className="na-applicant-role">
                                  {[r.jobTitle, r.company].filter(Boolean).join(' · ')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="na-target-cell">
                              <span className="na-target-name">{r.targetName}</span>
                              {r.targetSub && (
                                <span className="na-target-sub">{r.targetSub}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`na-status-pill na-status-${r.status}`}>
                              {r.status}
                            </span>
                          </td>
                          <td>
                            <span className="na-slot-cell">
                              {formatPreferredSlot(r.preferredDate, r.preferredTime, r.userTimezone)}
                            </span>
                          </td>
                          <td>
                            {isVisa && r.call ? (
                              <div className="na-call-cell">
                                <span className={`na-call-pill na-call-${r.call.status}`}>
                                  {CALL_STATUS_LABEL[r.call.status] || r.call.status}
                                </span>
                                <span className="na-call-meta">
                                  #{r.call.attemptNumber}/8
                                </span>
                              </div>
                            ) : (
                              <span className="na-dash">—</span>
                            )}
                          </td>
                          <td>
                            <div className="na-actions-cell">
                              {!r.standaloneProposal && (
                              <button
                                className="btn-icon"
                                onClick={() => handleStartCall(r.id, r.kind)}
                                disabled={callingId === r.id}
                                title={`Browser test call (${r.kind})`}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                              </button>
                              )}
                              {!r.standaloneProposal && (
                              <button
                                className="btn-icon na-btn-real-call"
                                onClick={() => handleStartRealCall(r.id, r.kind)}
                                disabled={callingId === r.id}
                                title={`Real phone call via Twilio (${r.kind})`}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 12.79V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h14a2 2 0 0 1 2-2.21z" transform="rotate(180 12 13.5)"/>
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                </svg>
                              </button>
                              )}
                              {/* PROPOSAL button removed — the setup proposal now
                                  lives as a tab inside the View page. Standalone
                                  proposals (no booking row) still open the editor
                                  directly since they have no detail page. */}
                              <button
                                className="btn-primary btn-sm"
                                onClick={() =>
                                  r.standaloneProposal
                                    ? navigate(`/naytive-applications/proposal/${r.proposalId}`)
                                    : navigate(`/naytive-applications/${r.kind}/${r.id}`)
                                }
                              >
                                VIEW
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`na-toast na-toast-${toast.type}`} onClick={() => setToast(null)}>
            {toast.message}
          </div>
        )}
      </main>

      {/* Live call modal */}
      {liveCall && (
        <div className="na-modal-overlay">
          <div className="na-modal na-call-modal" onClick={(e) => e.stopPropagation()}>
            <div className="na-modal-header">
              <span className="na-modal-title">
                Live call · request #{liveCall.requestId}
              </span>
              <button className="na-modal-close" onClick={handleHangUp} title="Hang up">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="na-modal-body">
              <div className={`na-call-status na-call-status-${liveCall.status}`}>
                {liveCall.status === 'starting' && 'Requesting microphone…'}
                {liveCall.status === 'connecting' && 'Connecting to Kim…'}
                {liveCall.status === 'connected' && '🔴 Live — Kim is listening'}
                {liveCall.status === 'ended' && 'Call ended.'}
                {liveCall.status === 'error' && `Error: ${liveCall.error || 'unknown'}`}
              </div>

              <div className="na-meter-row">
                <div className="na-meter-label">
                  <span className="na-meter-dot" style={{ background: '#3B82F6' }} />
                  You
                </div>
                <AudioLevelMeter
                  stream={micStream}
                  color="#3B82F6"
                  active={liveCall.status === 'connected'}
                />
              </div>
              <div className="na-meter-row">
                <div className="na-meter-label">
                  <span className="na-meter-dot" style={{ background: '#DC2626' }} />
                  Kim
                </div>
                <AudioLevelMeter
                  stream={remoteStream}
                  color="#DC2626"
                  active={liveCall.status === 'connected'}
                />
              </div>

              <p className="na-call-tip">
                Speak into your mic — bars on the left light up when you talk.
                Kim's audio plays through your speakers, with the right meter
                showing her voice level. If a meter never moves, that side
                isn't flowing.
              </p>
              <audio ref={audioRef} autoPlay playsInline />
              <button className="na-hangup-btn" onClick={handleHangUp}>
                Hang up
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attempts modal */}
      {activeAttempts && (
        <div className="na-modal-overlay" onClick={() => setActiveAttempts(null)}>
          <div className="na-modal" onClick={(e) => e.stopPropagation()}>
            <div className="na-modal-header">
              <span className="na-modal-title">Call attempts — request #{activeAttempts.requestId}</span>
              <button className="na-modal-close" onClick={() => setActiveAttempts(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="na-modal-body">
              {activeAttempts.loading && <div className="na-modal-empty">Loading…</div>}
              {!activeAttempts.loading && activeAttempts.items.length === 0 && (
                <div className="na-modal-empty">No attempts yet for this request.</div>
              )}
              {!activeAttempts.loading && activeAttempts.items.map((a) => (
                <div className="na-attempt-card" key={a.id}>
                  <div className="na-attempt-head">
                    <span className="na-attempt-num">Attempt #{a.attemptNumber}</span>
                    <span className={`na-attempt-outcome na-out-${a.outcome}`}>{a.outcome}</span>
                    <span className="na-attempt-time">{formatDateTime(a.attemptedAt)}</span>
                    {a.durationSeconds && <span className="na-attempt-dur">{a.durationSeconds}s</span>}
                  </div>
                  {a.fieldsConfirmed && Object.keys(a.fieldsConfirmed).length > 0 && (
                    <div className="na-attempt-section">
                      <div className="na-attempt-label">Confirmed</div>
                      <div className="na-attempt-chips">
                        {Object.keys(a.fieldsConfirmed).map((k) => (
                          <span key={k} className="na-chip na-chip-confirmed">{k}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {a.fieldsMissing && a.fieldsMissing.length > 0 && (
                    <div className="na-attempt-section">
                      <div className="na-attempt-label">Missing</div>
                      <div className="na-attempt-chips">
                        {a.fieldsMissing.map((m, i) => (
                          <span key={i} className="na-chip na-chip-missing">
                            {typeof m === 'string' ? m : (m?.field || m?.docTypeCode || JSON.stringify(m))}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {a.transcript && Array.isArray(a.transcript) && a.transcript.length > 0 && (
                    <details className="na-transcript">
                      <summary>Transcript ({a.transcript.length} turns)</summary>
                      <div className="na-transcript-body">
                        {a.transcript.map((t, i) => (
                          <div key={i} className={`na-turn na-turn-${t.role}`}>
                            <span className="na-turn-role">{t.role}:</span>
                            <span className="na-turn-text">{t.text}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default NaytiveApplications;
